import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@/services/axios-base-query';
import { COURSE_ROUTES, USER_ROUTES } from '@/constants';

import type { Client, OrganizationMembershipResponse, User } from '@/types';
import type { Board } from '@/types/api/course.types';
import type {
  OrganizationProfileRequestType,
  RequestToCreateOrganizationType,
} from '@/types/user-organization.types';

export const clientApi = createApi({
  reducerPath: 'OrganizationApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'Organizations',
    'OrgScope',
    'OrgProfile',
    'ClientUsers',
    'ClientBoards',
  ],
  endpoints: (builder) => ({
    getUserOrganizations: builder.query<OrganizationMembershipResponse, void>({
      query: () => ({
        url: USER_ROUTES.GET_USER_ORGANIZATIONS,
        method: 'GET',
      }),
      providesTags: ['Organizations', 'OrgProfile'],
    }),
    // tenant scoped scope endpoint
    getScope: builder.query<any, { tenant: string }>({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_SCOPE(tenant),
        method: 'GET',
      }),
      providesTags: ['OrgScope'],
    }),

    // tenant scoped profile update
    updateOrganizationProfile: builder.mutation<
      any, // @note for now this is not used so no need to return anything and if you see any on other endpoints that means it is still not in use
      { tenant: string; data: Partial<OrganizationProfileRequestType> }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ORGANIZATION_CLIENT_PROFILE_UPDATE(tenant),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['OrgProfile'],
    }),

    // CREATE ORG (GLOBAL)
    addUserOrganization: builder.mutation<
      Client,
      Partial<RequestToCreateOrganizationType>
    >({
      query: (data) => ({
        url: USER_ROUTES.ADD_USER_ORGANIZATION_GLOBAL,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Organizations'],
    }),

    // tenant scoped template update
    updateOrganizationTemplate: builder.mutation<
      any,
      { tenant: string; data: FormData }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ORGANIZATION_TEMPLATE_UPDATE(tenant),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Organizations'],
    }),
    getAllClientUserList: builder.query<User[], { tenant: string }>({
      query: ({ tenant }) => ({
        url: USER_ROUTES.GET_ALL_CLIENT_USER_LIST(tenant),
        method: 'GET',
      }),
      providesTags: ['ClientUsers'],
    }),
    addClientUser: builder.mutation<
      unknown,
      { tenant: string; data: { user_roles: [string]; email: string } }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ADD_CLIENT_USER(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ClientUsers'],
    }),
    updateClientUserRoles: builder.mutation<
      unknown,
      { tenant: string; roleId: number; data: { user_roles: [string] } }
    >({
      query: ({ tenant, roleId, data }) => ({
        url: USER_ROUTES.UPDATE_CLIENT_USER_ROLES(tenant, roleId),
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['ClientUsers'],
    }),
    deleteClientUser: builder.mutation<
      unknown,
      { tenant: string; userId: string }
    >({
      query: ({ tenant, userId }) => ({
        url: USER_ROUTES.DELETE_CLIENT_USER(tenant, userId),
        method: 'DELETE',
      }),
      invalidatesTags: ['ClientUsers'],
    }),
    addClientUsersInBulk: builder.mutation<
      unknown,
      { tenant: string; data: FormData }
    >({
      query: ({ tenant, data }) => ({
        url: USER_ROUTES.ADD_CLIENT_USERS_BULK(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ClientUsers'],
    }),
    getClientBoards: builder.query<
      Board[],
      {
        tenant: string;
        boardEnrollStudent?: string;
        teacherBoardTeacher?: string;
      }
    >({
      query: ({ tenant, boardEnrollStudent = '', teacherBoardTeacher = '' }) => ({
        url: COURSE_ROUTES.GET_BOARDS(tenant),
        method: 'GET',
        params: {
          parent: '',
          level: '',
          board_enroll__student: boardEnrollStudent,
          teacher_board__teacher: teacherBoardTeacher,
        },
      }),
      providesTags: ['ClientBoards'],
    }),
    addTeacherToBoard: builder.mutation<
      unknown,
      {
        tenant: string;
        data: {
          teachers: Array<{
            teacher: string;
            board: number;
            active: boolean;
          }>;
        };
      }
    >({
      query: ({ tenant, data }) => ({
        url: COURSE_ROUTES.POST_BOARD_TEACHERS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ClientBoards'],
    }),
    addStudentToBoard: builder.mutation<
      unknown,
      {
        tenant: string;
        data: {
          students: Array<{
            student: string;
            board: number;
            semester_course: null;
            active: boolean;
          }>;
        };
      }
    >({
      query: ({ tenant, data }) => ({
        url: COURSE_ROUTES.GET_ENROLL_STUDENTS(tenant),
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ClientBoards'],
    }),
    addPublicImageResource: builder.mutation<any, { data: FormData }>({
      query: ({ data }) => ({
        url: USER_ROUTES.ADD_PUBLIC_IMAGE_RESOURCE(),
        method: 'POST',
        data,
      }),
    }),
    getPublicImageResource: builder.query<
      any,
      { userId: string; pageSize?: number }
    >({
      // if pageZize is not then it will fetch all
      query: ({ userId, pageSize }) => ({
        url: USER_ROUTES.GET_PUBLIC_IMAGE_RESOURCE(userId, pageSize),
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetScopeQuery,
  useUpdateOrganizationProfileMutation,
  useAddUserOrganizationMutation,
  useUpdateOrganizationTemplateMutation,
  useGetUserOrganizationsQuery,
  useGetAllClientUserListQuery,
  useAddClientUserMutation,
  useUpdateClientUserRolesMutation,
  useDeleteClientUserMutation,
  useAddClientUsersInBulkMutation,
  useGetClientBoardsQuery,
  useAddTeacherToBoardMutation,
  useAddStudentToBoardMutation,
  useAddPublicImageResourceMutation,
  useGetPublicImageResourceQuery,
} = clientApi;
